import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";

/**
 * FeeBreakdown — transparent seller take-home calculator.
 *
 * Given a price (dollars, string or number) it fetches the platform fee
 * schedule from /marketplace/config and shows exactly what Stripe keeps,
 * what Servrevu keeps, and what the seller nets.
 *
 * Props:
 *  - priceDollars: current price input (string or number)
 *  - shippingDollars: optional shipping the buyer pays (also included in fee base)
 *  - variant: "full" (default, itemised block for Sell page) | "compact" (single-line for cards) | "info" (marketing-style card)
 */
export default function FeeBreakdown({ priceDollars, shippingDollars = 0, variant = "full" }) {
  const [fees, setFees] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/marketplace/config")
      .then(({ data }) => !cancelled && setFees(data.fees))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (!fees) return null;

  const price = Math.max(0, parseFloat(priceDollars || "0") || 0);
  const shipping = Math.max(0, parseFloat(shippingDollars || "0") || 0);
  const gross = price + shipping;
  const grossCents = Math.round(gross * 100);

  const stripePct = fees.stripe_fee_bp / 100; // e.g. 2.9
  const platformPct = fees.platform_fee_bp / 100; // e.g. 2.0
  const stripeFixedDollars = fees.stripe_fixed_cents / 100; // e.g. 0.30

  const stripeCents = gross > 0 ? Math.round((grossCents * fees.stripe_fee_bp) / 10000) + fees.stripe_fixed_cents : 0;
  const platformCents = gross > 0 ? Math.round((grossCents * fees.platform_fee_bp) / 10000) : 0;
  const netCents = Math.max(0, grossCents - stripeCents - platformCents);

  const fmt = (c) => `$${(c / 100).toFixed(2)}`;

  if (variant === "info") {
    return (
      <div className="rounded-2xl border border-[#EAE3DE] bg-white p-5" data-testid="fee-breakdown-info">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-[#F9E4E1] p-2 text-[#B31E1E]">
            <Info size={16} />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-[#1B1614]">Transparent processing fees</div>
            <p className="text-xs text-[#5C4E48] mt-0.5">
              Sellers only pay when a sale completes. No listing fees, no monthly fees.
            </p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-xl border border-[#EAE3DE] bg-[#FAF5F2] p-3">
                <div className="text-[11px] font-mono tracking-wide text-[#8B7B73] uppercase">Stripe processing</div>
                <div className="mt-1 text-2xl font-black text-[#1B1614]">
                  {stripePct.toFixed(1)}<span className="text-lg">%</span>
                  <span className="text-sm text-[#5C4E48] font-semibold ml-1">+ ${stripeFixedDollars.toFixed(2)}</span>
                </div>
                <div className="text-[11px] text-[#8B7B73] mt-1">Card networks & Stripe — deducted at payout</div>
              </div>
              <div className="rounded-xl border border-[#EAE3DE] bg-[#FAF5F2] p-3">
                <div className="text-[11px] font-mono tracking-wide text-[#8B7B73] uppercase">Servrevu marketplace</div>
                <div className="mt-1 text-2xl font-black text-[#B31E1E]">
                  {platformPct.toFixed(1)}<span className="text-lg">%</span>
                </div>
                <div className="text-[11px] text-[#8B7B73] mt-1">Powers fraud checks, disputes & moderation</div>
              </div>
            </div>
            <p className="mt-3 text-[11px] text-[#8B7B73] leading-relaxed">
              Example: on a <span className="font-mono">$100.00</span> sale, Stripe keeps
              <span className="font-mono"> $3.20</span> and Servrevu keeps
              <span className="font-mono"> $2.00</span> — seller receives
              <span className="font-mono font-semibold text-[#1B1614]"> $94.80</span> directly to their bank.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (variant === "compact") {
    return (
      <div className="text-[11px] text-[#8B7B73] leading-snug" data-testid="fee-breakdown-compact">
        Sellers pay {stripePct.toFixed(1)}% + ${stripeFixedDollars.toFixed(2)} Stripe processing and {platformPct.toFixed(1)}% Servrevu fee.
        {gross > 0 && <> On this sale seller nets <span className="font-mono font-semibold text-[#1B1614]">{fmt(netCents)}</span>.</>}
      </div>
    );
  }

  // Full (Sell-page) — live calculator
  return (
    <TooltipProvider delayDuration={100}>
      <div className="rounded-xl border border-[#EAE3DE] bg-[#FAF5F2] p-3.5" data-testid="fee-breakdown-full">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-[#1B1614]">
          You&apos;ll receive
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" className="inline-flex items-center text-[#8B7B73] hover:text-[#B31E1E]" data-testid="fee-tooltip-trigger">
                <Info size={12} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[260px] text-[11px] leading-snug">
              Stripe deducts card processing (2.9% + $0.30) from your payout, and Servrevu keeps a
              2% marketplace fee — 4.9% total, only on completed sales.
            </TooltipContent>
          </Tooltip>
        </div>
        <div className="mt-1 text-2xl font-black text-[#1B1614] font-mono" data-testid="fee-breakdown-net">
          {fmt(netCents)}
        </div>

        <div className="mt-2.5 space-y-1 text-[11px] text-[#5C4E48]">
          <Row label="Sale price" value={fmt(Math.round(price * 100))} />
          {shipping > 0 && <Row label="Shipping (buyer pays)" value={fmt(Math.round(shipping * 100))} />}
          <div className="my-1 border-t border-dashed border-[#EAE3DE]" />
          <Row
            label={`Stripe processing (${stripePct.toFixed(1)}% + $${stripeFixedDollars.toFixed(2)})`}
            value={`− ${fmt(stripeCents)}`}
            muted
          />
          <Row
            label={`Servrevu fee (${platformPct.toFixed(1)}%)`}
            value={`− ${fmt(platformCents)}`}
            muted
          />
        </div>
      </div>
    </TooltipProvider>
  );
}

function Row({ label, value, muted }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className={muted ? "text-[#8B7B73]" : ""}>{label}</span>
      <span className={`font-mono ${muted ? "text-[#8B7B73]" : "text-[#1B1614] font-semibold"}`}>{value}</span>
    </div>
  );
}
